const UNSORTED_ARROW = "& .MuiTableSortLabel-root[aria-label^='Sort by ']";

/**
 * A header's column menu shown only while the reader is on the header, and no sort arrow on a column
 * the table is not sorted by.
 *
 * Beside the label they take 47px of every heading: a 23px sort arrow and a 24px menu button. In a
 * narrow pane that is the difference between a table that fits and one that scrolls, and a column
 * sized for its label alone cuts it to "Fixe" (#6524). Hidden until wanted, the label has the
 * heading's whole width. The menu then lies at the heading's right-hand edge, on the header's own
 * background, rather than beside the label. Keyboard focus shows it as hover does, and a device without hover always
 * shows it, since nothing would reveal it there.
 *
 * The arrow of the column the table *is* sorted by stays: it says something about the rows. An unsorted
 * column's arrow says only that the column can be sorted, and in a column sized to its label there is
 * nowhere for it to appear on hover but under the menu, so with a mouse it does not appear at all. On
 * a touch screen it always does, since there is no hover to find it by. A click on the
 * label still sorts, as it always has, and the menu lists the sort orders by name.
 *
 * The menu is drawn on the header's own colour, which MRT sets on the cell itself; `inherit` would
 * read the transparent box in between. Pass `table.options.mrtTheme.baseBackgroundColor`.
 *
 * The proposals table hides its menus the same way (#6512), but still shows an unsorted arrow on hover.
 */
export function headerToolsOnHoverSx(headerBackground: string) {
  return {
    "& .Mui-TableHeadCell-Content-Actions": {
      backgroundColor: headerBackground,
      opacity: 0,
      position: "absolute",
      // In the cell's own right padding, so a column sized to its label and a sorted arrow keeps both
      // in sight while the menu shows.
      right: "2px",
      top: "50%",
      transform: "translateY(-50%)",
      transition: "opacity 150ms",
    },
    // MRT names an unsorted column's arrow "Sort by …" and a sorted one's "Sorted by …". The label is
    // the hook rather than the SyncAlt icon inside it, because a production build drops MUI's icon test
    // ids and a selector on them would match nothing there.
    [UNSORTED_ARROW]: {
      display: "none",
    },
    "&:hover, &:focus-within": {
      "& .Mui-TableHeadCell-Content-Actions": { opacity: 1 },
    },
    // A touch screen has no hover to reveal either, so it always shows both: the sort arrow and the
    // column menu (#6715).
    "@media (hover: none)": {
      "& .Mui-TableHeadCell-Content-Actions": { opacity: 1 },
      [UNSORTED_ARROW]: { display: "inline-flex" },
    },
  } as const;
}
